'use client'

import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { sendLoginEvent } from 'analytics/events/recommended/login/login'
import { useFieldValidations } from 'country-configuration/services/client/useFieldValidations'
import { FormProvider, useForm } from 'form-components'
import { Checkbox as AutoLoginCheckbox } from 'form-components/components/Checkbox/Checkbox'
import { EmailInput } from 'form-components/components/EmailInput/EmailInput'
import { PasswordInput } from 'form-components/components/PasswordInput/PasswordInput'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useLogin } from 'login/hooks/useLogin'
import type { LoginFormProps } from 'login/types/login'
import { useEffect } from 'react'

import {
	FormErrorTypes,
	FormFieldIds,
	FormFieldNames,
	LoginSumbitErrors,
	TestIds,
	defaultValues,
} from '../../constants'
import { GenericEvents } from '../../constants/analytics'
import type { FormValues } from '../../types/form'
import { GenericError } from './GenericError/GenericError'

import styles from './Form.module.scss'

export function LoginForm({ isCheckout }: LoginFormProps): React.ReactNode {
	const { login, loginSuccess, loginErrors } = useLogin(isCheckout)
	const { t } = useLabels()
	const { email, password } = useFieldValidations()

	const { regex, ...passwordRules } = password
	const formMethods = useForm({
		defaultValues,
		mode: 'onTouched',
	})
	const { handleSubmit, setError } = formMethods

	const onSubmit = (values: FormValues): Promise<void> => login(values)

	useEffect(() => {
		sendGenericEvent(GenericEvents.LOGIN_SHOW)
	}, [])

	useEffect(() => {
		if (loginSuccess) {
			const loginSuccessPayload = {
				logins: 1,
			}

			sendLoginEvent(loginSuccessPayload)
		}
	}, [loginSuccess])

	useEffect(() => {
		if (loginErrors) {
			loginErrors.forEach((error) => {
				sendGenericEvent(`login_post_validation_${error}`)
				setError(error === LoginSumbitErrors.Generic ? 'root' : error, {
					type: FormErrorTypes.Validate,
					message: t(`form.${error}.format.errormessage`),
				})
			})
		}
	}, [loginErrors])

	return (
		<FormProvider {...formMethods}>
			<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<EmailInput
					name={FormFieldNames.Email}
					id={FormFieldIds.Email}
					rules={email}
					data-testid={TestIds.EmailInput}
					className={styles.email}
				/>
				<PasswordInput
					name={FormFieldNames.Password}
					id={FormFieldIds.Password}
					rules={passwordRules}
					data-testid={TestIds.PasswordInput}
				/>
				<AutoLoginCheckbox
					name={FormFieldNames.AutoLogin}
					id={FormFieldIds.AutoLogin}
					label={t('login.remember.checkbox')}
					data-testid={TestIds.AutoLoginCheckbox}
					className={styles.autoLoginCheckBox}
				/>
				<GenericError />
				<SubmitButton
					id={FormFieldIds.LoginButton}
					data-testid={TestIds.LoginButton}
				>
					{t('signin.signin.button')}
				</SubmitButton>
			</form>
		</FormProvider>
	)
}
